/**
 * 2026-10-14 (owner) · continuous monitoring, in the right panel.
 *
 * Opened by clicking a monitoring under the process tree, or by "+ Add continuous monitoring"
 * (the selected step lets go; the operation stays). Two parts:
 *
 *   · the monitoring itself — what is monitored (its parameters), From step (its start) →
 *     To step (its end). Who records it, where, and what each reading records are the parameters' own. One row per field: its label, and the input beside it.
 *     Saved through /bp/recipes/<id>/spans/ (span_view.py).
 *   · its parameters — each a PARAMETER_RECORD step owned by the monitoring. "Add parameter"
 *     creates one and opens it in the same step form every parameter record uses, so it is
 *     configured exactly as that step type is: the parameter, the fields it records, how it is
 *     monitored (interval, number of readings, limits), its rules. Clicking a parameter reopens it.
 *
 * On the floor every step from the From step's start to the To step's end shows these parameters
 * as tabs beside the step (span_window.py), each read on its own interval.
 *
 * A HOLD TIME BETWEEN OPERATIONS is the other kind (services/run/span_clock.py): a span whose body is
 * one HOLD step carrying a CLOCK rule. It runs from the From operation's start or end to the To
 * operation's start or end — "end of compounding → start of pre-filtration, NMT 32 h" — and a breach
 * is a deviation that may also hold the batch or block the start of the To operation. Authored here
 * with its own fields (anchors, limit, on breach); the floor never works it by hand.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, InputNumber, Popconfirm, Select, Typography, message } from "antd";
import { ChevronRight, Trash2 } from "lucide-react";
import Axios from "axios";
import { addSpan, deleteSpan } from "@/utils/record_order_api";
import { groupedParameters } from "./StepForm";

const { Text } = Typography;
export const NEW_SPAN = "__new_span__";

const help = { fontSize: "calc(12.5px * var(--dz-fs, 1))", color: "var(--dz-faint)" };

/** One field: its label on the left, the input beside it. */
function Row({ label, children }) {
  return (
    <div className="u0-spanrow">
      <span className="l">{label}</span>
      <div className="v">{children}</div>
    </div>
  );
}

/** Every STEP of an operation, in the record's order, as "CODE · title". */
function stepsOf(op) {
  const out = [];
  const walk = (list) => (list || []).forEach((n) => {
    if (n.kind === "STEP") out.push({ value: n.code, label: `${n.code} · ${n.title || ""}` });
    walk(n.children);
  });
  walk(op?.children);
  return out;
}

const patchSpan = async (recipeId, code, body) => {
  const token = localStorage.getItem("Token");
  try {
    const res = await Axios.patch(`/bp/recipes/${recipeId}/spans/${encodeURIComponent(code)}/`, body,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    return res?.data?.data;
  } catch (e) {
    throw new Error(e?.response?.data?.message || "Could not save it.");
  }
};

/** 2026-10-15 (owner) · a reading in continuous monitoring is timed, so it must say how often:
 *  Reading schedule = Periodic, Every N — the server blocks the record without it
 *  (readiness.continuous_without_interval). An event item (a log, a check, a sample) needs none. */
const needsInterval = (n) => n.step_type === "PARAMETER_RECORD"
  && !((n.monitoring || {}).schedule === "Periodic" && Number((n.monitoring || {}).every) > 0);

/** What a parameter is set to, in one line — the step form holds the rest. */
function summaryOf(n, masters) {
  const m = n.monitoring || {};
  const p = (masters.parameters || []).find((x) => x.id === n.parameter_id);
  const parts = [
    p ? p.name : n.parameter_id ? null : "no parameter yet",
    m.schedule === "Periodic" && m.every ? `every ${m.every} ${m.every_unit || "min"}` : m.schedule || null,
    m.readings ? `${m.readings} reading${Number(m.readings) === 1 ? "" : "s"}` : null,
    m.min != null || m.max != null ? `${m.min ?? "—"} – ${m.max ?? "—"}${p?.unit ? ` ${p.unit}` : ""}` : null,
  ];
  return parts.filter(Boolean).join(" · ");
}

/** The CLOCK rule on a hold time's HOLD step, as the record sends it (params in seconds). */
const clockOf = (n) => (n?.rules || []).find((r) => r.rule_type === "CLOCK") || null;
const hoursOf = (seconds) => (seconds == null ? null : Math.round((Number(seconds) / 3600) * 100) / 100);
/** Hours as the bind endpoint takes a duration: "HH:MM:SS" (hours may exceed 24). */
const hms = (hours) => {
  if (hours == null || hours === "") return null;
  const total = Math.max(0, Math.round(Number(hours) * 3600));
  const h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), sec = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};
const BREACH_OPTIONS = [
  { value: "RAISE_ITEM", label: "Raise a deviation" },
  { value: "HOLD", label: "Raise a deviation and hold the batch" },
  { value: "BLOCK", label: "Raise a deviation and block the start of the To operation" },
];
const ANCHOR_OPTIONS = [{ value: "START", label: "its start" }, { value: "END", label: "its end" }];

export default function SpanPanel({ span, recipeId, operations = [], masters = {}, readOnly = false,
  onSaved, onClose, onInsertParam, onOpenParam, onDeleteStep, onReload, onEditStep,
  onInsertHold, onBindRule, onUnbindRule }) {
  const isNew = span?.id === NEW_SPAN;
  const holdStep = (span?.children || []).find((n) => n.kind === "STEP" && n.step_type === "HOLD") || null;
  const [kindChoice, setKindChoice] = useState(null);
  useEffect(() => { setKindChoice(null); }, [span?.id]);
  const isHold = isNew ? kindChoice === "hold" : !!holdStep;
  const opById = useMemo(() => Object.fromEntries(operations.map((o) => [o.id, o])), [operations]);
  const opByCode = useMemo(() => Object.fromEntries(operations.map((o) => [o.code, o])), [operations]);
  const blank = () => ({
    name: span?.name || "", monitors: span?.monitors || "", records: span?.records || "", who: span?.role || "",
    from_operation_id: span?.from_operation_id ?? opByCode[span?.from?.operation]?.id ?? operations[0]?.id,
    to_operation_id: span?.to_operation_id ?? opByCode[span?.to?.operation]?.id ?? operations[operations.length - 1]?.id,
    from_step_code: span?.from_step || null, to_step_code: span?.to_step || null,
    from_anchor: (span?.from?.at || "start").toUpperCase(), to_anchor: (span?.to?.at || "end").toUpperCase(),
    on_breach_effect: span?.on_breach?.effect || "RAISE_ITEM",
    min_hours: hoursOf((clockOf(holdStep)?.params || {}).min_duration),
    max_hours: hoursOf((clockOf(holdStep)?.params || {}).max_duration),
    // 2026-10-15 (owner) · how often each parameter is read — in the form, saved onto every parameter's
    // monitoring (Reading schedule = Periodic, Every N) with the rest of the monitoring.
    ...(() => {
      const m = ((span?.children || []).find((n) => n.kind === "STEP" && (n.monitoring || {}).schedule === "Periodic"
        && Number((n.monitoring || {}).every) > 0) || {}).monitoring || {};
      return { every: m.every ? Number(m.every) : null, every_unit: m.every_unit || "min" };
    })(),
  });
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  // What is monitored: the parameters, picked as the Parameter record step picks one. Each is a
  // parameter record step of this monitoring — picked here, it is made; un-picked, it goes.
  const params = (span?.children || []).filter((n) => n.kind === "STEP");
  const savedIds = params.map((n) => n.parameter_id).filter((x) => x != null);
  const [picked, setPicked] = useState(savedIds);
  useEffect(() => { setF(blank()); setPicked(savedIds); }, [span?.id, span?.pk, savedIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  const nameOfParam = (id) => (masters.parameters || []).find((p) => p.id === id)?.name || "";
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const text = (k, placeholder, max = 200) => (
    <Input size="small" value={f[k]} onChange={(e) => set(k)(e.target.value)} maxLength={max}
           placeholder={placeholder} disabled={readOnly} />
  );
  const opOptions = operations.map((o) => ({ value: o.id, label: `${o.number ?? ""}. ${o.name}` }));
  const fromSteps = stepsOf(opById[f.from_operation_id]);
  const toSteps = stepsOf(opById[f.to_operation_id]);

  /** A hold time: the span (anchors, effect), its HOLD step if it has none yet, and its CLOCK limit. */
  const saveHold = async () => {
    if (!f.name.trim()) { message.error("Name the hold time — e.g. Unfiltered bulk hold NMT 32 h."); return; }
    const lo = f.min_hours === "" ? null : f.min_hours, hi = f.max_hours === "" ? null : f.max_hours;
    if (lo == null && hi == null) { message.error("Give it a limit — a minimum, a maximum or both."); return; }
    if (lo != null && hi != null && Number(lo) > Number(hi)) { message.error("The minimum is above the maximum."); return; }
    setBusy(true);
    try {
      const body = { name: f.name.trim(), kind: "CONTINUOUS",
                     from_operation_id: f.from_operation_id, from_anchor: f.from_anchor, from_step_code: "",
                     to_operation_id: f.to_operation_id, to_anchor: f.to_anchor, to_step_code: "",
                     on_breach_effect: f.on_breach_effect };
      const res = isNew ? await addSpan(recipeId, body) : await patchSpan(recipeId, span.id, body);
      const code = res?.id || span?.id;
      let stepCode = holdStep?.code;
      if (!stepCode) {
        const made = await onInsertHold?.(code, { title: body.name });
        if (!made?.ok || !made.created) throw new Error(made?.message || "The hold step could not be added.");
        stepCode = made.created;
      }
      const old = clockOf(holdStep);
      const same = old && hoursOf((old.params || {}).min_duration) === (lo == null ? null : Number(lo))
        && hoursOf((old.params || {}).max_duration) === (hi == null ? null : Number(hi)) && old.name === body.name;
      if (!same) {
        if (old) await onUnbindRule?.(stepCode, old.id);
        const bound = await onBindRule?.(stepCode, { rule_type: "CLOCK", name: body.name,
                                                     min_duration: hms(lo), max_duration: hms(hi) });
        if (bound && bound.ok === false) throw new Error(bound.message || "The limit could not be saved.");
      }
      message.success(isNew ? "Hold time added." : "Saved.");
      await onSaved?.(code);
    } catch (e) {
      message.error(e?.message || "Could not save it.");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (isHold) { await saveHold(); return; }
    if (!picked.length) { message.error("Choose what is monitored — at least one parameter."); return; }
    if (!(Number(f.every) > 0)) { message.error("Set how often it is read — Reading every N min."); return; }
    setBusy(true);
    try {
      const name = picked.map(nameOfParam).filter(Boolean).join(", ") || f.name;
      const { every, every_unit, ...spanFields } = f;
      const body = { ...spanFields, name, from_step_code: f.from_step_code || "", to_step_code: f.to_step_code || "",
                     from_anchor: "START", to_anchor: "END" };
      const res = isNew ? await addSpan(recipeId, body) : await patchSpan(recipeId, span.id, body);
      const code = res?.id || span?.id;
      // Each parameter picked and not yet a step of it becomes one; one un-picked goes.
      const made = [];
      for (const id of picked.filter((x) => !savedIds.includes(x))) {
        const r = await onInsertParam?.(code, { parameter_id: id, title: nameOfParam(id) || "Parameter", open: false });
        if (r?.created) made.push(r.created);
      }
      // the interval onto every parameter that stays — each one's monitoring row, as its own form writes it
      const keep = params.filter((x) => x.parameter_id != null && picked.includes(x.parameter_id));
      for (const n of keep) {
        const m = n.monitoring || {};
        if (m.schedule === "Periodic" && Number(m.every) === Number(every) && (m.every_unit || "min") === every_unit) continue;
        await onEditStep?.(n.code, { monitoring: { ...m, schedule: "Periodic", every, every_unit } });
      }
      for (const c of made) {
        await onEditStep?.(c, { monitoring: { schedule: "Periodic", every, every_unit } });
      }
      for (const n of params.filter((x) => x.parameter_id != null && !picked.includes(x.parameter_id))) {
        await onDeleteStep?.(n.code);
      }
      message.success(isNew ? "Continuous monitoring added." : "Saved.");
      await onSaved?.(code);
    } catch (e) {
      message.error(e?.message || "Could not save it.");
    } finally {
      setBusy(false);
    }
  };

  // 2026-10-15 (owner) · Remove takes the monitoring away whole: its parameters first (the server keeps a
  // monitoring with parameters in it), then the monitoring itself. Stops at the first refusal, saying why.
  const remove = async () => {
    try {
      for (const n of params) {
        const r = await onDeleteStep?.(n.code);
        if (r && r.ok === false) {
          message.error(r.message || `Could not remove ${n.code}.`);
          onReload?.();
          return;
        }
      }
      await deleteSpan(recipeId, span.id);
      message.success("Removed.");
      onClose?.();
      onReload?.();
    } catch (e) {
      message.error(e?.message || "Could not remove it.");
    }
  };

  return (
    <div className="u0-spanpanel" data-testid="span-panel">
      <div className="u0-stepid" data-family="structure">
        <div className="u0-stepid-title" style={{ cursor: "default" }}>
          {isNew ? (isHold ? "New hold time between operations" : "New continuous monitoring") : span.name}
        </div>
        {!isNew ? (
          <div className="u0-stepid-row">
            <span className="dz-code">{span.id}</span>
            <span>{params.length} parameter{params.length === 1 ? "" : "s"}</span>
          </div>
        ) : null}
      </div>

      <div className="u0-spanform">
        {isNew ? (
          <Row label="What is it">
            <Select size="small" style={{ width: "100%" }} value={kindChoice || "monitoring"} data-testid="span-kind"
                    onChange={setKindChoice}
                    options={[{ value: "monitoring", label: "Continuous monitoring — readings beside the steps" },
                              { value: "hold", label: "Hold time between operations — a clock" }]} />
          </Row>
        ) : null}
        {isHold ? (
          <>
            <Row label="Name">{text("name", "Unfiltered bulk hold NMT 32 h")}</Row>
            <Row label="Starts at">
              <div style={{ display: "flex", gap: 8, width: "100%" }}>
                <Select size="small" style={{ flex: 1 }} value={f.from_operation_id} options={opOptions} disabled={readOnly}
                        onChange={set("from_operation_id")} data-testid="hold-from" />
                <Select size="small" style={{ width: 110 }} value={f.from_anchor} options={ANCHOR_OPTIONS}
                        disabled={readOnly} onChange={set("from_anchor")} />
              </div>
            </Row>
            <Row label="Stops at">
              <div style={{ display: "flex", gap: 8, width: "100%" }}>
                <Select size="small" style={{ flex: 1 }} value={f.to_operation_id} options={opOptions} disabled={readOnly}
                        onChange={set("to_operation_id")} data-testid="hold-to" />
                <Select size="small" style={{ width: 110 }} value={f.to_anchor} options={ANCHOR_OPTIONS}
                        disabled={readOnly} onChange={set("to_anchor")} />
              </div>
            </Row>
            <Row label="Limit (hours)">
              <div style={{ display: "flex", gap: 8, alignItems: "center", width: "100%" }}>
                <InputNumber size="small" min={0} step={0.5} style={{ width: 120 }} value={f.min_hours} disabled={readOnly}
                             placeholder="not less than" onChange={set("min_hours")} data-testid="hold-min" />
                <InputNumber size="small" min={0} step={0.5} style={{ width: 120 }} value={f.max_hours} disabled={readOnly}
                             placeholder="not more than" onChange={set("max_hours")} data-testid="hold-max" />
              </div>
            </Row>
            <Row label="On breach">
              <Select size="small" style={{ width: "100%" }} value={f.on_breach_effect} options={BREACH_OPTIONS}
                      disabled={readOnly} onChange={set("on_breach_effect")} data-testid="hold-breach" />
            </Row>
          </>
        ) : (<>
        <Row label="What is monitored">
          {/* 2026-10-14 (owner) · the Parameter record step type's own picker: the parameter
              master, grouped by category, each with its type and unit. */}
          <Select size="small" mode="multiple" style={{ width: "100%" }} disabled={readOnly}
                  value={picked} onChange={setPicked} options={groupedParameters(masters.parameters || [])}
                  placeholder="Choose the parameters monitored" showSearch optionFilterProp="label"
                  allowClear data-testid="span-what" />
        </Row>
        <Row label="From operation">
          <Select size="small" style={{ width: "100%" }} value={f.from_operation_id} options={opOptions} disabled={readOnly}
                  onChange={(v) => setF((p) => ({ ...p, from_operation_id: v, from_step_code: null }))} />
        </Row>
        <Row label="From step (start)">
          <Select size="small" style={{ width: "100%" }} value={f.from_step_code ?? undefined} options={fromSteps} allowClear
                  placeholder="Start of the operation" onChange={(v) => set("from_step_code")(v || null)} disabled={readOnly}
                  showSearch optionFilterProp="label" />
        </Row>
        <Row label="To operation">
          <Select size="small" style={{ width: "100%" }} value={f.to_operation_id} options={opOptions} disabled={readOnly}
                  onChange={(v) => setF((p) => ({ ...p, to_operation_id: v, to_step_code: null }))} />
        </Row>
        <Row label="To step (end)">
          <Select size="small" style={{ width: "100%" }} value={f.to_step_code ?? undefined} options={toSteps} allowClear
                  placeholder="End of the operation" onChange={(v) => set("to_step_code")(v || null)} disabled={readOnly}
                  showSearch optionFilterProp="label" />
        </Row>
        <Row label="Reading every">
          <div style={{ display: "flex", gap: 8, alignItems: "center", width: "100%" }} data-testid="span-every">
            <InputNumber size="small" min={1} style={{ width: 110 }} value={f.every} disabled={readOnly}
                         placeholder="N" onChange={(v) => set("every")(v)} status={!(Number(f.every) > 0) ? "error" : undefined} />
            <Select size="small" style={{ width: 90 }} value={f.every_unit} disabled={readOnly}
                    options={[{ value: "min", label: "min" }, { value: "hrs", label: "hrs" }]} onChange={set("every_unit")} />
            <span style={{ fontSize: 12.5, color: Number(f.every) > 0 ? "var(--dz-muted, #64748b)" : "var(--dz-block, #cf1322)" }}>
              {Number(f.every) > 0 ? "alert 20 s before each reading" : "required"}
            </span>
          </div>
        </Row>
        </>)}
        {!readOnly ? (
          <div className="u0-spanbtns">
            <Button size="small" type="primary" onClick={save} loading={busy}>{isNew ? "Add monitoring" : "Save"}</Button>
            <Button size="small" onClick={onClose}>{isNew ? "Cancel" : "Close"}</Button>
            {!isNew ? (
              <Popconfirm title={params.length
                            ? `Remove "${span.name}" and its ${params.length} parameter${params.length === 1 ? "" : "s"}?`
                            : `Remove "${span.name}"?`}
                          okText="Remove" okButtonProps={{ danger: true }} onConfirm={remove}>
                <Button size="small" danger type="text" style={{ marginLeft: "auto" }}>Remove</Button>
              </Popconfirm>
            ) : null}
          </div>
        ) : null}
      </div>

      {isHold ? (
        <div className="u0-spanparams">
          <Text style={help}>
            Runs by itself on the floor: it starts when the From operation {String(f.from_anchor).toLowerCase() === "start" ? "starts" : "ends"} and
            stops when the To operation {String(f.to_anchor).toLowerCase() === "start" ? "starts" : "ends"}. The tablet shows the time left and
            alerts before the limit; a breach is a deviation{f.on_breach_effect === "HOLD" ? " and holds the batch"
              : f.on_breach_effect === "BLOCK" ? " and the To operation cannot be started" : ""}.
          </Text>
        </div>
      ) : (
      <div className="u0-spanparams">
        <div className="u0-eyebrow" style={{ display: "flex", alignItems: "center" }}>
          <span style={{ flex: 1 }}>Parameters recorded{isNew ? "" : ` · ${params.length}`}</span>
        </div>
        {isNew ? (
          <Text style={help}>Pick what is monitored above and press "Add monitoring" — each parameter is then
            listed here; open it to set its interval, number of readings and limits in the Parameter record form.</Text>
        ) : params.length ? params.map((n) => (
          <React.Fragment key={n.code}>
          <div className="u0-spanparam" data-testid="span-param">
            <button type="button" className="u0-spanparam-open" onClick={() => onOpenParam?.(n.code)}>
              <span className="dz-code">{n.code}</span>
              <span className="t">{n.title || "Parameter"}</span>
              <span className="s">{summaryOf(n, masters)}</span>
              <ChevronRight size={15} />
            </button>
            {!readOnly ? (
              <Popconfirm title="Remove this parameter?" okText="Remove" onConfirm={() => onDeleteStep?.(n.code)}>
                <Button size="small" type="text" danger icon={<Trash2 size={14} />} aria-label="Remove parameter" />
              </Popconfirm>
            ) : null}
          </div>
          {n.step_type === "PARAMETER_RECORD" && !n.parameter_id ? (
            <button type="button" onClick={() => onOpenParam?.(n.code)} data-testid="span-no-parameter"
                    style={{ display: "block", width: "100%", textAlign: "left", border: "1px solid #fecaca", background: "#fef2f2",
                             color: "var(--dz-block, #cf1322)", borderRadius: 6, padding: "6px 10px", margin: "-2px 0 6px",
                             fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              ⚠ No parameter chosen — open it and choose one.
            </button>
          ) : needsInterval(n) && readOnly ? (
            <button type="button" onClick={() => onOpenParam?.(n.code)} data-testid="span-no-interval"
                    style={{ display: "block", width: "100%", textAlign: "left", border: "1px solid #fecaca", background: "#fef2f2",
                             color: "var(--dz-block, #cf1322)", borderRadius: 6, padding: "6px 10px", margin: "-2px 0 6px",
                             fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              ⚠ Interval not set — required. Open it and set Reading schedule = Periodic, Every N min.
            </button>
          ) : null}
          </React.Fragment>
        )) : (
          <Text style={help}>No parameters yet — pick them under "What is monitored" and Save.</Text>
        )}
        {!isNew ? (
          <Text style={help}>
            On the floor, every step from {span.from_step || "the start of the From operation"} to{" "}
            {span.to_step || "the end of the To operation"} shows these as tabs beside the step.
          </Text>
        ) : null}
      </div>
      )}
    </div>
  );
}
